import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Bell, PhoneCall, UserPlus, CheckCheck } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

function timeAgo(iso) {
  if (!iso) return "";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export function NotificationBell() {
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  const loadCount = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      setCount(data.count || 0);
    } catch { /* non-critical */ }
  }, []);

  const loadItems = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data);
      setCount(data.filter((n) => !n.is_read).length);
    } catch { /* non-critical */ }
  }, []);

  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, 30000);
    return () => clearInterval(t);
  }, [loadCount]);

  useEffect(() => { if (open) loadItems(); }, [open, loadItems]);

  const openNotification = (n) => {
    setOpen(false);
    setItems((prev) => prev.map((p) => (p.id === n.id ? { ...p, is_read: true } : p)));
    if (!n.is_read) setCount((c) => Math.max(0, c - 1));
    api.post(`/notifications/${n.id}/read`).catch(() => {});
    if (n.type === "lead" && n.lead_id) navigate(`/app/leads?lead=${n.lead_id}`);
    else if (n.call_id) navigate(`/app/calls?call=${n.call_id}`);
    else navigate("/app/calls");
  };

  const markAll = () => {
    setItems((prev) => prev.map((p) => ({ ...p, is_read: true })));
    setCount(0);
    api.post("/notifications/read-all").catch(() => {});
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          className="relative h-10 w-10 inline-flex items-center justify-center rounded-md hover:bg-accent transition-colors"
          data-testid="notifications-bell-btn"
          aria-label="Notifications"
        >
          <Bell className="w-4 h-4" />
          {count > 0 && (
            <span
              data-testid="notifications-badge"
              className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold leading-4 text-center"
            >
              {count > 99 ? "99+" : count}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80" data-testid="notifications-dropdown">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Notifications</span>
          {items.some((n) => !n.is_read) && (
            <button
              onClick={markAll}
              data-testid="notifications-mark-all-btn"
              className="text-xs font-normal text-primary hover:underline inline-flex items-center gap-1"
            >
              <CheckCheck className="w-3.5 h-3.5" /> Mark all as read
            </button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <div className="max-h-[360px] overflow-y-auto">
          {items.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground" data-testid="notifications-empty">
              No notifications yet.
            </div>
          )}
          {items.map((n) => (
            <DropdownMenuItem
              key={n.id}
              data-testid={`notification-item-${n.id}`}
              className={`flex items-start gap-3 px-3 py-2.5 cursor-pointer ${!n.is_read ? "bg-muted/50" : ""}`}
              onClick={() => openNotification(n)}
            >
              <div className="mt-0.5 shrink-0">
                {n.type === "lead" ? (
                  <UserPlus className="w-4 h-4 text-emerald-500" />
                ) : (
                  <PhoneCall className="w-4 h-4 text-blue-500" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`text-sm truncate ${!n.is_read ? "font-semibold" : ""}`}>{n.title}</span>
                  {!n.is_read && <span className="w-1.5 h-1.5 rounded-full bg-red-500 shrink-0" />}
                </div>
                <div className="text-xs text-muted-foreground truncate">{n.description}</div>
                <div className="text-[10px] text-muted-foreground mt-0.5">{timeAgo(n.created_at)}</div>
              </div>
            </DropdownMenuItem>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
